import React, { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Search,
  Filter,
  Download,
  Eye,
} from "lucide-react";
import { FactureViewerModal } from "./FactureViewerModal";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { downloadFactureFile } from "../../utils/factureDownload";

export interface Facture {
  id: string;
  numero: string;
  fournisseur_nom: string;
  client_nom?: string;
  montant_ttc: number;
  montant_ht?: number;
  tva?: number;
  montant_avance?: number;
  montant_net?: number;
  frais_escompte?: number;
  commission_succes?: number;
  date_emission: string;
  date_echeance: string;
  statut: string;
  score_decision?: number;
  decision_paiement?: string;
  valide_par?: string;
  fichier_original?: string;
}

interface FactureListProps {
  factures: Facture[];
  onFactureClick: (facture: Facture) => void;
  onDownload?: (facture: Facture) => void;
  renderActions?: (facture: Facture) => React.ReactNode;
  isLoading?: boolean;
}

const statusConfig = {
  soumise: {
    color: "bg-warning-100 text-warning-800",
    icon: Clock,
    label: "En attente",
  },
  validee: {
    color: "bg-success-100 text-success-800",
    icon: CheckCircle,
    label: "Validée",
  },
  rejetee_client: {
    color: "bg-error-100 text-error-800",
    icon: XCircle,
    label: "Rejetée",
  },
  approuvee: {
    color: "bg-primary-100 text-primary-800",
    icon: CheckCircle,
    label: "Approuvée",
  },
  payee: {
    color: "bg-success-100 text-success-800",
    icon: CheckCircle,
    label: "Payée",
  },
};

export const FactureList: React.FC<FactureListProps> = ({
  factures,
  onFactureClick,
  renderActions,
  onDownload,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedForView, setSelectedForView] = useState<Facture | null>(null);
  const { t } = useTranslation();

  const handleDownload = async (facture: Facture) => {
    if (onDownload) return onDownload(facture);
    try {
      const ok = await downloadFactureFile(facture.id, facture.numero);
      if (!ok) toast.error(t("factures.management.noFile"));
    } catch (error) {
      console.error("Erreur téléchargement", error);
      toast.error(t("factures.management.downloadError"));
    }
  };

  const handleOpenViewer = (e: React.MouseEvent, facture: Facture) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedForView(facture);
    setViewerOpen(true);
  };

  const filteredFactures = factures.filter((facture) => {
    const matchesSearch =
      facture.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
      facture.fournisseur_nom.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || facture.statut === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(amount || 0);
  };

  const getStatusConfig = (statut: string) => {
    return (
      statusConfig[statut as keyof typeof statusConfig] || statusConfig.soumise
    );
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* En-tête avec filtres */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Factures
            </h3>
            {!isLoading && (
              <p className="mt-1 text-sm text-gray-500">
                {filteredFactures.length} facture(s) trouvée(s)
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            {/* Recherche */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filtre statut */}
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="soumise">En attente</option>
              <option value="validee">Validée</option>
              <option value="approuvee">Approuvée</option>
              <option value="payee">Payée</option>
              <option value="rejetee_client">Rejetée</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste des factures */}
      <div className="divide-y divide-gray-200">
        {!isLoading && filteredFactures.map((facture) => {
          const config = getStatusConfig(facture.statut);
          const StatusIcon = config.icon;

          return (
            <div
              key={facture.id}
              className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onFactureClick(facture)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <FileText className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-4 min-w-0 flex-1">
                    <div className="flex items-center">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {facture.numero}
                      </h4>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
                      >
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {config.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {facture.fournisseur_nom}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center text-xs text-gray-500">
                      <span>
                        Émise le {format(new Date(facture.date_emission), "dd/MM/yyyy")}
                      </span>
                      <span className="mx-2">•</span>
                      <span>
                        Échéance le {format(new Date(facture.date_echeance), "dd/MM/yyyy")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 ml-4">
                  <div className="hidden sm:block text-right">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(facture.montant_ttc)}
                    </div>
                    <div className="text-xs text-gray-500">TTC</div>
                  </div>

                  {renderActions && (
                    <div className="flex items-center">
                      {renderActions(facture)}
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-1">
                    <button 
                      className="p-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-full transition-colors"
                      title="Visualiser"
                      onClick={(e) => handleOpenViewer(e, facture)}
                    >
                      <Eye className="h-5 w-5" />
                    </button>

                    <button 
                      className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                      title="Télécharger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(facture);
                      }}
                    >
                      <Download className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )}

      {!isLoading && filteredFactures.length === 0 && (
        <div className="px-4 py-12 text-center text-gray-500">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-2" />
          <p>Aucune facture trouvée</p>
        </div>
      )}

      {selectedForView && (
        <FactureViewerModal
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          factureUrl={selectedForView.fichier_original || ""}
          factureNumero={selectedForView.numero}
        />
      )}
    </div>
  );
};
